<script>
export default { inheritAttrs: false }
</script>

<script setup>
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from 'vue'
import {router, usePage} from "@inertiajs/vue3";
import Timeout from "@/Components/Reels/Timeout.vue";
import Toast from "@/Components/Common/Toast.vue";
import StarOverlay from "@/Components/StarOverlay.vue";
import {
    incrementStarModalVideoSwipeCount,
    getStarModalVideoSwipeCount,
    setThirdModalShown,
    setNinthModalShown,
    setDontShowAgainFlag, trackSwipeCycle, setSwipeCyclePause, trackSwipeTriggers
} from "@/Helpers/starModalCookie.js";
import "@/../assets/js/lti-analytics-0.2.0.min.js"
import '../../../assets/js/lti-analytics-0.2.0.min';
import {useJoinOptions} from "@/composables/useJoinOptions";
import NavigationArrows from "@/Components/Reels/NavigationArrows.vue";
import VideoPlayer from "@/Components/Reels/VideoPlayer.vue";
import VideoControls from "@/Components/Reels/VideoControls.vue";
import VideoOverlay from "@/Components/Reels/VideoOverlay.vue";
import DesktopSidebar from "@/Components/Reels/DesktopSidebar.vue";
import JoinModal from "@/Components/Common/JoinModal.vue";
import GiveStarsModal from "@/Components/Reels/GiveStarsModal.vue";
import FirstStarModal from "@/Components/Reels/FirstStarModal.vue";
import { getMainAuditionsUrl, trackAuditionAnalyticsEvent } from "@/Helpers/auditions";
import { ArrowLeft } from "lucide-vue-next";
import KebabMenu from "@/Components/Reels/KebabMenu.vue";
import JoinGoldModal from "@/Components/Common/JoinGoldModal.vue";
import BuyStarsModal from "@/Components/Reels/BuyStarsModal.vue";
import BuyoutSuccessModal from "@/Components/Reels/BuyoutSuccessModal.vue";
import CommentPromptModal from "@/Components/Reels/CommentPromptModal.vue";
import TopSponsorsSheet from "@/Components/Reels/TopSponsorsSheet.vue";
import StarPowerModal from "@/Components/Reels/StarPowerModal.vue";
import SfwJoinModal from "@/Components/Common/SfwJoinModal.vue";
import {getFathomPostPrefix, trackFathomEvent} from "@/Helpers/analytics";
import { openStarPackPurchaseUrl } from "@/Helpers/starPackPurchase";
import axios from "axios";
import CreateCastingWizardModal from "@/Components/Casting/CreateCastingWizardModal.vue";
import CastingSignupModal from "@/Components/Casting/CastingSignupModal.vue";

// Sponsor data fetched from the API
const topSponsorsData = ref({ data: [], total: 0, current_page: 1, last_page: 1 })
const showSponsorsSheet = ref(false)

// Fetch top sponsors for a given post
const fetchTopSponsors = async (postId) => {
    if (!postId) return
    try {
        const response = await axios.get(route(props.routePrefix + '.reels.top-sponsors', { post: postId }))
        topSponsorsData.value = response.data
    } catch (error) {
        console.error('Failed to load top sponsors:', error)
    }
}

// Import the constants for video swipe trigger counts
const VIDEO_SWIPE_TRIGGER_COUNT_3RD = 3;
const VIDEO_SWIPE_TRIGGER_COUNT_9TH = 9;

const pageProps = defineProps({
    initialPostId: Number,
    sort: String,
    page: Number,
    next_page: Number,
    currentIndex: Number,
    data: Array,
    tag: Object,
    // allTags: Array,
    perPage: Number,
    specificPost: Boolean,
    timeoutSettings: Object,
    timeoutLimitReached: Boolean,
    hasReelsSubscription: Boolean,
    read_from_cache: Boolean,
    cache_key: String,
    is_randomized_feed: Boolean,
    // likes: Array,
    // api_key: String,
    // api_url: String,
    moreAuditions: Array,
    starPacks: Array,
    buyNowPurchaseBaseUrl: String,
    userHasCommented: Boolean,
    castingsSettings: Object,
})

const { clearJoinOptions } = useJoinOptions()
const { props } = usePage()

// Handle browser back button to preserve scroll position
const handlePopState = () => {
    // When user hits browser back button, ensure we navigate with preserveScroll
    if (window.location.pathname.includes('/feed')) {
        // Still on feed page, do nothing
        return
    }
    // If we came from another page (e.g. ViewCasting), let Inertia restore it naturally
    if (sessionStorage.getItem('feedReferrer')) {
        sessionStorage.removeItem('feedReferrer')
        return
    }
    // If we're going back to index, ensure scroll position is preserved
    router.visit(route(props.routePrefix + '.reels.index'), {
        preserveScroll: true,
        preserveState: false,
    })
}

const isUserLoggedIn = ref(!!(props.auth && props.auth.user && props.auth.user.id !== null))
// Use the prop value directly instead of creating a new ref
const hasReelsSubscription = computed(() => pageProps.hasReelsSubscription || (props.auth && props.auth.has_reels_subscription))
const isTrialUser = computed(() => props.auth && props.auth.is_trial)
const walletBalance = ref(Number(props.auth?.user?.wallet_balance) || 0)
const isGoldUser = ref(props.auth?.is_gold || false)
const videoContainerRef = ref(null)
const nextPage = ref(pageProps.next_page || 1)
const requiresAgeVerification = ref(props.requiresAgeVerification)

const showToast = ref(false)
const toastText = ref('')

// Get current video data for meta tags
const currentVideoData = computed(() => {
    if (videos.value && videos.value.length > 0 && currentIndex.value !== undefined && videos.value[currentIndex.value]) {
        return videos.value[currentIndex.value]
    }
    return null
})

const isCurrentPostExpired = computed(() => currentVideoData.value?.feed_post_status == 3)

// Generate meta information based on current video
const metaInfo = computed(() => {
    if (!currentVideoData.value) {
        return {
            title: 'NA Feeds',
            description: 'Watch exclusive content on NA Feeds',
            canonicalUrl: 'https://www.naughtyamerica.com/feed',
            ogTitle: 'NA Feeds',
            ogDescription: 'Watch exclusive content on NA Feeds',
            ogUrl: 'https://www.naughtyamerica.com/feed',
            ogImage: 'https://sm.naughtycdn.com/images/feeds/Open_Graph_image.png',
            ogSiteName: 'NA Feeds',
            twitterTitle: 'NA Feeds',
            twitterDescription: 'Watch exclusive content on NA Feeds',
            twitterImage: 'https://sm.naughtycdn.com/images/feeds/Open_Graph_image.png',
            structuredData: {
                "@context": "https://schema.org",
                "@type": "VideoObject",
                "name": "NA Feeds",
                "description": "Watch exclusive content on NA Feeds",
                "thumbnailUrl": "",
                "uploadDate": new Date().toISOString(),
                "contentUrl": "",
                "embedUrl": "https://www.naughtyamerica.com/feed"
            }
        }
    }

    const performerName = currentVideoData.value.creator?.name || 'Performer'
    const videoTitle = currentVideoData.value.title || 'Video'
    const videoDescription = currentVideoData.value.description || ''
    const thumbnailUrl = currentVideoData.value.start_card_image || ''
    const uploadDate = currentVideoData.value.created_at || new Date().toISOString()
    const videoSlug = currentVideoData.value.slug || ''
    const tagSlug = currentTag.value.slug
    const tagTitle = currentTag.value.name
    const metaUrl = tagSlug === 'all' ?
        `https://www.naughtyamerica.com/feed/video/${videoSlug}` :
        `https://www.naughtyamerica.com/feed/video/${videoSlug}?tag=${tagSlug}`

    return {
        title: `${tagTitle} - Porn Videos`,
        description: `${tagTitle} | Watch now – exclusive on Naughty America.`,
        canonicalUrl: metaUrl,
        ogTitle: `${tagTitle} - Porn Videos`,
        ogDescription: 'Visit NaughtyAmerica.com and start watching now',
        ogUrl: metaUrl,
        ogImage: thumbnailUrl,
        ogSiteName: 'Naughty America',
        twitterTitle: `${tagTitle} - Porn Videos`,
        twitterDescription: 'Visit NaughtyAmerica.com and start watching now',
        twitterImage: thumbnailUrl,
        videoTitle: `${tagTitle} - Watch ${videoTitle}`,
        videoDescription: videoDescription,
        videoUrl: metaUrl,
        videoImage: thumbnailUrl,
        videoUploadDate: uploadDate,
        structuredData: {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": `${tagTitle} - Watch ${videoTitle}`,
            "description": videoDescription,
            "thumbnailUrl": thumbnailUrl,
            "uploadDate": uploadDate,
            "contentUrl": (currentVideoData.value.videoUrl_mp4 || '').split('?')[0],
            "embedUrl": metaUrl
        }
    }
})

const loadMoreThreshold = pageProps.perPage
const params = new URLSearchParams(window.location.search)
const isDebugMode = ref(params.get('debug') === '1' || import.meta.env.VITE_REELS_DEBUG === 'true')
const swipeLimit = ref(pageProps.timeoutSettings.swipe.active ? pageProps.timeoutSettings.swipe.limit : -1)
const timeLimit = ref(pageProps.timeoutSettings.time.active ? pageProps.timeoutSettings.time.limit : -1)

// Optional feature flag: when true, once the limit is reached, lock ALL videos (even those previously viewed)
const LOCK_ALL_AFTER_LIMIT_STORAGE_KEY = 'na_reels_lock_all_after_limit'
const storedLockAllAfterLimit = (() => {
    try {
        const raw = localStorage.getItem(LOCK_ALL_AFTER_LIMIT_STORAGE_KEY)
        return raw === '1' ? true : raw === '0' ? false : null
    } catch (e) {
        return null
    }
})()
const paramLockAll = params.get('lockall')
const lockAllAfterLimit = ref(
    paramLockAll === '1' ? true :
        paramLockAll === '0' ? false :
            (storedLockAllAfterLimit !== null ? storedLockAllAfterLimit : pageProps.timeoutSettings.locked_All_videos.active)
)
const LIMIT_REACHED_STORAGE_KEY = 'na_reels_limit_reached'
const storedLimitReached = (() => {
    try {
        return localStorage.getItem(LIMIT_REACHED_STORAGE_KEY) === '1'
    } catch (e) {
        return false
    }
})()
const timeoutLimitReached = ref(
    swipeLimit.value === -1 && timeLimit.value === -1
        ? false
        : (pageProps.timeoutLimitReached || storedLimitReached)
)

// Persist the set of unlocked video IDs so unlocked items remain playable across navigation
const UNLOCKED_IDS_STORAGE_KEY = 'na_reels_unlocked_ids'
const unlockedVideoIds = ref([])
// Prevent double-counting swipes: track the last video ID we incremented for
const lastBumpedVideoId = ref(null)
// Persist the set of video IDs that have already been counted as a swipe this session
const COUNTED_IDS_STORAGE_KEY = 'na_reels_counted_ids'
const countedVideoIds = ref([])
const fathomTimeoutViewMessage = ref(props.routePrefix === 'members2' ?
    'Members Timeout Load' :
    'Tour Timeout Load'
)
const fathomTimeoutClickMessage = ref(props.routPrefix === 'members2' ?
    'Members Timeout Click' :
    'Tour Timeout Click'
)
const joinDefaultSource = 'Audition_Page'
const joinGoldDefaultSource = 'Audition_Page_Kebab_Menu'
const joinGoldUpgradeSource = ref('')
const joinStarPopupSource = ref('')
const joinStarPowerSource = ref('')
const joinCreateCastingSource = ref('')
const buyStarsSource = ref('')

function loadCountedIdsFromStorage() {
    try {
        const raw = localStorage.getItem(COUNTED_IDS_STORAGE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) {
                countedVideoIds.value = parsed
            }
        }
    } catch (e) {
        // ignore storage errors
    }
}

function saveCountedIdsToStorage() {
    try {
        localStorage.setItem(COUNTED_IDS_STORAGE_KEY, JSON.stringify(countedVideoIds.value))
    } catch (e) {
        // ignore storage errors
    }
}

function loadUnlockedIdsFromStorage() {
    try {
        const raw = localStorage.getItem(UNLOCKED_IDS_STORAGE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) {
                unlockedVideoIds.value = parsed
            }
        }
    } catch (e) {
        // ignore storage errors
    }
}

function saveUnlockedIdsToStorage() {
    try {
        localStorage.setItem(UNLOCKED_IDS_STORAGE_KEY, JSON.stringify(unlockedVideoIds.value))
    } catch (e) {
        // ignore storage errors
    }
}

loadUnlockedIdsFromStorage()
loadCountedIdsFromStorage()

function recordUnlockedVideoId(video) {
    if (!video || video.id == null || video.is_ad) return
    // Respect swipe limit: only persist up to N unlocked items
    if (swipeLimit.value > 0 && unlockedVideoIds.value.length >= swipeLimit.value) return
    if (!unlockedVideoIds.value.includes(video.id)) {
        unlockedVideoIds.value = [...unlockedVideoIds.value, video.id]
        saveUnlockedIdsToStorage()
    }
}

// Determine if a specific video index should be treated as locked by timeout rules
const shouldVideoBeLocked = computed(() => (videoIndex) => {
    if (requiresAgeVerification.value) {
        return true
    }
    if (hasReelsSubscription.value && !isTrialUser.value) {
        return false
    }
    if (!timeoutLimitReached.value) {
        return false
    }
    // If configured, lock everything upon reaching the limit
    if (lockAllAfterLimit.value) {
        return true
    }
    // If there is a swipe limit configured, only the first N viewed videos stay unlocked
    if (swipeLimit.value > 0) {
        const videoId = videos.value?.[videoIndex]?.id
        if (videoId == null) {
            return true
        }
        return !unlockedVideoIds.value.includes(videoId)
    }
    // If no swipe limit is set, lock all when timeout is reached
    return true
})

const shouldAutoplay = function(index) {
    const isExpired = videos.value[index]?.feed_post_status == 3
    const shouldPlayNow = !isExpired && !shouldVideoBeLocked.value(index) && isCurrentVideo(index);
    if (shouldPlayNow && isDebugMode.value) {
        console.log('>>> Should play now: ' + shouldPlayNow)
    }
    return shouldPlayNow;
}

const showTimeoutPopup = ref(false)
const timeoutStorageKey = 'reels-timeout'
const timeoutRef = ref(null)
const timeoutPopupShowCount = ref(0)
const currentSwipeCount = ref(0)

// Star modal state
const showStarModal = ref(false)
const starButtonHighlighted = ref(false)

// Persist and enforce behavior when toggling lock-all-after-limit at runtime
watch(lockAllAfterLimit, (newVal) => {
    try { localStorage.setItem(LOCK_ALL_AFTER_LIMIT_STORAGE_KEY, newVal ? '1' : '0') } catch (e) {}
})

const clearTimeoutClick = () => {
    if (isDebugMode.value) {
        axios.post(route(props.routePrefix + '.reels.clear-timeout')).then(response => {
            console.log('Clear timeout response:', response)
            if (response.data === 'Ok') {
                showTimeoutPopup.value = false
                timeoutLimitReached.value = false
                countedVideoIds.value = []
                unlockedVideoIds.value = []
                currentSwipeCount.value = 0
                if (timeoutRef.value) {
                    timeoutRef.value.restartTimer()
                }
                try { localStorage.setItem(LIMIT_REACHED_STORAGE_KEY, '0') } catch (e) {}
                try { localStorage.setItem(COUNTED_IDS_STORAGE_KEY, '[]') } catch (e) {}
                try { localStorage.setItem(UNLOCKED_IDS_STORAGE_KEY, '[]') } catch (e) {}
            }
        });
    }
}

const videos = ref(pageProps.data)
const currentTag = ref(pageProps.tag)
const isMobileDevice = ref(false)
const transitionClass = ref('')

// 🔥 SIMPLIFIED: Use only currentIndex, removed duplicate currentVideo ref
const currentIndex = ref(pageProps.currentIndex)
let nextLoading = false

const updateMetaTags = () => {
    if (!metaInfo.value) return

    // Add or update title
    let titleElement = document.querySelector('title')
    if (!titleElement) {
        titleElement = document.createElement('title')
        document.head.appendChild(titleElement)
    }
    titleElement.textContent = metaInfo.value.title

    // Add or update meta description
    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
        metaDescription = document.createElement('meta')
        metaDescription.name = 'description'
        document.head.appendChild(metaDescription)
    }
    metaDescription.content = metaInfo.value.description

    // Add or update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]')
    if (!canonicalLink) {
        canonicalLink = document.createElement('link')
        canonicalLink.rel = 'canonical'
        document.head.appendChild(canonicalLink)
    }
    canonicalLink.href = metaInfo.value.canonicalUrl

    // Add or update Open Graph meta tags
    const ogTags = [
        { property: 'og:title', content: metaInfo.value.ogTitle },
        { property: 'og:description', content: metaInfo.value.ogDescription },
        { property: 'og:url', content: metaInfo.value.ogUrl },
        { property: 'og:image', content: metaInfo.value.ogImage },
        { property: 'og:site_name', content: metaInfo.value.ogSiteName },
        { property: 'og:type', content: 'video.other' }
    ]

    // Add video-specific meta tags if available
    if (metaInfo.value.videoTitle) {
        ogTags.push({ property: 'og:video:title', content: metaInfo.value.videoTitle })
    }
    if (metaInfo.value.videoDescription) {
        ogTags.push({ property: 'og:video:description', content: metaInfo.value.videoDescription })
    }
    if (metaInfo.value.videoUrl) {
        ogTags.push({ property: 'og:video:url', content: metaInfo.value.videoUrl })
    }
    if (metaInfo.value.videoImage) {
        ogTags.push({ property: 'og:video:image', content: metaInfo.value.videoImage })
    }
    if (metaInfo.value.videoUploadDate) {
        ogTags.push({ property: 'og:video:upload_date', content: metaInfo.value.videoUploadDate })
    }

    ogTags.forEach(tag => {
        let metaTag = document.querySelector(`meta[property="${tag.property}"]`)
        if (!metaTag) {
            metaTag = document.createElement('meta')
            metaTag.setAttribute('property', tag.property)
            document.head.appendChild(metaTag)
        }
        metaTag.content = tag.content
    })

    // Add or update Twitter Card meta tags
    const twitterTags = [
        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: metaInfo.value.twitterTitle },
        { name: 'twitter:description', content: metaInfo.value.twitterDescription },
        { name: 'twitter:image', content: metaInfo.value.twitterImage }
    ]

    // Add Twitter video meta tags if available
    if (metaInfo.value.videoTitle) {
        twitterTags.push({ name: 'twitter:title', content: metaInfo.value.videoTitle })
    }
    if (metaInfo.value.videoDescription) {
        twitterTags.push({ name: 'twitter:description', content: metaInfo.value.videoDescription })
    }

    twitterTags.forEach(tag => {
        let metaTag = document.querySelector(`meta[name="${tag.name}"]`)
        if (!metaTag) {
            metaTag = document.createElement('meta')
            metaTag.name = tag.name
            document.head.appendChild(metaTag)
        }
        metaTag.content = tag.content
    })

    // Add structured data
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]')
    if (!structuredDataScript) {
        structuredDataScript = document.createElement('script')
        structuredDataScript.type = 'application/ld+json'
        document.head.appendChild(structuredDataScript)
    }
    structuredDataScript.textContent = JSON.stringify(metaInfo.value.structuredData)
}

// Watch functions - moved here after all variable declarations
watch(() => pageProps.data, (newData) => {
    videos.value = newData
}, { immediate: true })

const onSharePopupUsed = () => {
    if (!isUserLoggedIn.value || !currentVideoData.value?.id) return
    axios.post('/api/sga/record-goal', { type: 'share_audition', audition_id: currentVideoData.value.id })
        .then(({ data }) => {
            if (data.sga_awarded) {
                window.dispatchEvent(new CustomEvent('show-sga-overlay', { detail: data.sga_awarded }))
            }
        })
        .catch(() => {})
}

onMounted(() => {
    if (requiresAgeVerification.value) {
        isSfwJoinModalOpen.value = true

        trackFathomEvent('SFW Audition Page Loaded')
        trackAuditionAnalyticsEvent('SFW Audition Page Loaded', currentVideoData.value)
    }

    document.body.classList.add('reels-feed-page')
    window.addEventListener('share-popup:used', onSharePopupUsed)

    // Fetch sponsor data for the initial video
    if (videos.value[currentIndex.value]?.id) {
        fetchTopSponsors(videos.value[currentIndex.value].id)
    }

    // Force scroll to top
    window.scrollTo(0, 0)

    // LOCK body scroll (so inner container scrolls)
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    // Load LTI Analytics script
    const script = document.createElement('script')
    script.src = 'https://an-sm.naughtycdn.com/js/lti-analytics-0.2.0.min.js'
    script.setAttribute('data-cmp-info', '13')
    script.setAttribute('data-cmp-ab', '2')
    document.head.appendChild(script)

    // Set up meta tags
    updateMetaTags()

    checkMobileDevice()
    document.body.classList.add('noScroll')

    // Initial setup
    transitionClass.value = 'transition-all duration50'

    // if (!pageProps.specificPost) {
    //     setTimeout(() => {
    //         window.history.replaceState({}, '', pageProps.tag.slug + '/' + videos.value[currentIndex.value].slug)
    //     }, 100)
    // }
    setTimeout(() => {
        const currentUrl = window.location.pathname;
        const properInitialUrl = '/feed/video/' + currentVideoData.value.slug;
        if (currentUrl !== properInitialUrl) {
            window.history.replaceState({}, '', properInitialUrl + window.location.search)
        }
    }, 100)

    // Auto-open the appropriate modal based on return URL params (mutually exclusive)
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('buyoutComplete') === '1') {
        urlParams.delete('buyoutComplete')
        urlParams.delete('openGiveStars')
        urlParams.delete('openFirstStar')
        const cleanSearch = urlParams.toString()
        const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '')
        window.history.replaceState({}, '', cleanUrl)

        nextTick(() => {
            isBuyoutSuccessModalOpen.value = true
        })
    } else if (urlParams.get('openGiveStars') === '1') {
        urlParams.delete('openGiveStars')
        const cleanSearch = urlParams.toString()
        const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '')
        window.history.replaceState({}, '', cleanUrl)

        nextTick(() => {
            isGiveStarsModalOpen.value = true
        })
    } else if (urlParams.get('openFirstStar') === '1') {
        urlParams.delete('openFirstStar')
        const cleanSearch = urlParams.toString()
        const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '')
        window.history.replaceState({}, '', cleanUrl)

        nextTick(() => {
            if (!isUserLoggedIn.value) {
                isStarPowerModalOpen.value = true
            } else if (walletBalance.value <= 0) {
                buyStarsSource.value = 'first_star'
                isBuyStarsModalOpen.value = true
            } else {
                isFirstStarModalOpen.value = true
            }
        })
    }

    if (urlParams.get('openFirstComment') === '1') {
        urlParams.delete('openFirstComment')
        const cleanSearch = urlParams.toString()
        const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '')
        window.history.replaceState({}, '', cleanUrl)

        nextTick(() => {
            isCommentPromptModalOpen.value = true
        })
    }

    // first video is counted as a swipe (first view)
    if (swipeLimit.value > 0) {
        const video = videos.value[currentIndex.value]
        recordUnlockedVideoId(video)
        if (video && !video.is_ad) {
            if (!countedVideoIds.value.includes(video.id)) {
                bumpSwipeCount(video)
                countedVideoIds.value = [...countedVideoIds.value, video.id]
                saveCountedIdsToStorage()
            }
            lastBumpedVideoId.value = video.id
        }
    }

    // Handle browser back button to preserve scroll position
    window.addEventListener('popstate', handlePopState)

    window.addEventListener('resize', handleWindowResize)
    window.addEventListener('orientationchange', handleWindowResize)

    nextTick(() => {
        if (videoContainerRef.value) {
            // Make the container focusable
            videoContainerRef.value.setAttribute('tabindex', '0')

            // ADD EVENT LISTENERS TO THE VIDEO CONTAINER ONLY
            videoContainerRef.value.addEventListener('keydown', handleKeyboardNavigation)
            // Wheel for desktop only (matches Feed.vue - touch handles mobile)
            if (!isMobileDevice.value) {
                videoContainerRef.value.addEventListener('wheel', handleWheelNavigation, { passive: false })
            }

            // Auto-focus the container so keyboard events work immediately
            videoContainerRef.value.focus()
        }
        updateSlideHeight()
        if (reelViewportRef.value) {
            resizeObserver = new ResizeObserver(updateSlideHeight)
            resizeObserver.observe(reelViewportRef.value)
            reelViewportRef.value.addEventListener('touchmove', onReelSwipeMove, { passive: false })
        }
    })
})

const cleanupMetaTags = () => {
    const selectors = [
        'meta[name="description"]',
        'link[rel="canonical"]',
        'meta[property^="og:"]',
        'meta[name^="twitter:"]',
        'script[type="application/ld+json"]',
    ]
    selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => el.remove())
    })
}

onBeforeUnmount(() => {
    if (isBackArrowNavigation.value) {
        // One final write before component unmounts
        sessionStorage.setItem('shouldRestoreBrowseState', 'true')
        sessionStorage.setItem('cameFromIndex', 'true')

        if (isDebugMode.value) {
            console.log('💀 onBeforeUnmount: Persisting flags one last time')
        }
    }

    cleanupMetaTags()

    document.body.classList.remove('reels-feed-page')
    document.body.classList.remove('noScroll')
    window.removeEventListener('share-popup:used', onSharePopupUsed)
    window.removeEventListener('popstate', handlePopState)
    window.removeEventListener('resize', handleWindowResize)
    window.removeEventListener('orientationchange', handleWindowResize)
     document.body.style.overflow = ''
    document.documentElement.style.overflow = ''

    if (videoContainerRef.value) {
        videoContainerRef.value.removeEventListener('keydown', handleKeyboardNavigation)
        videoContainerRef.value.removeEventListener('wheel', handleWheelNavigation)
    }
    if (resizeObserver && reelViewportRef.value) {
        resizeObserver.unobserve(reelViewportRef.value)
        reelViewportRef.value.removeEventListener('touchmove', onReelSwipeMove)
    }
})

const checkMobileDevice = () => {
    const userAgent = window.navigator.userAgent
    const mobileKeywords = [
        'Android',
        'webOS',
        'iPhone',
        'iPad',
        'iPod',
        'BlackBerry',
        'Windows Phone',
    ]

    isMobileDevice.value = mobileKeywords.some(keyword => userAgent.includes(keyword))
}

// Function to check if a video index is the current video
const isCurrentVideo = (index) => {
    return index === currentIndex.value
}

function nextRoute() {
    //const video = videos.value[videos.value.length - 1]
    // Initial post video when accessing feed
    const video = videos.value.find(v => v.id === pageProps.initialPostId)
    return route(props.routePrefix + '.reels.feed.next', {
        tag: pageProps.tag.slug,
        post: video.slug,
        sort: pageProps.sort,
        page: nextPage.value,
    });
}

function handleTimeLimitReached(currentVideo) {
    if (hasReelsSubscription.value && !isTrialUser.value) {
        return
    }
    axios.post(route(props.routePrefix + '.reels.timeout'), {
        limit_reached: true,
    }).then(response => {
        const wasNotTimeout = !timeoutLimitReached.value;
        timeoutLimitReached.value = response.data.limit_reached;
        showTimeoutPopup.value = response.data.limit_reached;

        // If we just entered timeout state, clean up current video only if it should be locked
        if (response.data.limit_reached && wasNotTimeout && videoRef.value) {
            trackFathomEvent(fathomTimeoutViewMessage.value);

            try { localStorage.setItem(LIMIT_REACHED_STORAGE_KEY, '1') } catch (e) {}
            const currentVideoIndex = parseInt(videoRef.value.getAttribute('data-video-id'))
            const videoIndex = videos.value.findIndex(v => v.id === currentVideoIndex)
            if (shouldVideoBeLocked.value(videoIndex)) {
                videoRef.value.pause();
                videoRef.value.currentTime = 0;
                // Ensure all videos are muted when locking
                setVideoMuteState(true);
            }
        }
    })
}

const trackPageView = () => {
    if (typeof window !== 'undefined' && window.fathom) {
        if (isDebugMode.value) {
            console.log('>> TRACKING PAGE VIEW')
        }
        window.fathom.trackPageview();
    }

    if (isDebugMode.value) {
        console.log('currentVideo', videos.value[currentIndex.value])
    }
};

// Star modal handlers
const handleStarModalClose = () => {
    showStarModal.value = false
    // Trigger star button pulse when modal closes
    highlightStarButton()
}

const handleStarModalDontShowAgain = () => {
    setDontShowAgainFlag()
}

const handleStarModalJoinClick = () => {
    // Track with custom parameter
    if (typeof fathom !== 'undefined') {
        trackFathomEvent('NA Auditions Modal')
    }

    // Redirect to join page using proper route
    window.location.href = route('www.join.signUp')
}

const handleStarModalStartStarring = () => {
    // Highlight the star button
    highlightStarButton()
}

const highlightStarButton = () => {
    starButtonHighlighted.value = true
    // Remove highlight after 2.5 seconds
    setTimeout(() => {
        starButtonHighlighted.value = false
    }, 2500)
}

// const likedVideos = ref(pageProps.likes)
const showSponsorModal = ref(false)

// Track logged video/category combinations to prevent infinite logging
const loggedCategoryCombinations = new Set()

const currentVideoHasCategory = (currentVideoIndex, categoryName) => {
    const video = videos.value[currentVideoIndex]
    if (video) {
        categoryName = categoryName.toLowerCase()
        const category = video.categories.find(category => category.name.toLowerCase() === categoryName)

        if (isDebugMode.value) {
            const logKey = `${currentVideoIndex}-${categoryName}`
            if (!loggedCategoryCombinations.has(logKey)) {
                console.log('currentVideoHasCategory', currentVideoIndex, categoryName, category)
                loggedCategoryCombinations.add(logKey)
            }
        }

        if (category) {
            return true
        }
    }

    return false
}

const displayToastMessage = (message, duration) => {
    showToast.value = true
    toastText.value = message

    setTimeout(() => {
        showToast.value = false
    }, duration)
}
const showStarVotingSuccess = ref(false)
const showStarVotingError = ref(false)
const starVotingErrorText = ref('')
const isSubmittingStars = ref(false)

const handleStarClick = (trigger = 'Star_Popup') => {
    if (trigger === 'Star_Popup') {
        trackAuditionAnalyticsEvent('Star Clicked', currentVideoData.value)
        trackFathomEvent('Star Clicked');
    }

    if (!isUserLoggedIn.value) {
        // isJoinModalOpen.value = true
        isStarPowerModalOpen.value = true
        joinStarPopupSource.value = trigger
        return
    }

    // If user has no stars, send them to buy star packs first
    if (walletBalance.value <= 0) {
        buyStarsSource.value = 'feed_no_balance'
        isBuyStarsModalOpen.value = true
        return
    }

    isGiveStarsModalOpen.value = true
}

const handleNearGoalClick = (remaining) => {
    giveStarsInitialAmount.value = remaining
    trackAuditionAnalyticsEvent('FOMO Pill Clicked', currentVideoData.value)
    trackFathomEvent('FOMO Pill Clicked')
    handleStarClick('Near_Goal_Pill')
}

const submitStars = (amount, comment = null, isBuyout = false, source = null, interactionMethod = null) => {
    let id = videos.value[currentIndex.value].id

    if (!isUserLoggedIn.value) {
        if (isDebugMode.value) {
            console.log('++ LTI Analytics: User not logged in, returning early');
        }
        showSponsorModal.value = true
        return
    }

    if (isDebugMode.value) {
        console.log('++ LTI Analytics: User is logged in, continuing...');
    }

    // Check if this is an auditions video for Fathom tracking
    const isAuditionsVideo = currentVideoHasCategory(currentIndex.value, 'Auditions')

    // likedVideos.value.push({ post_id: id })
    // likedVideos.value = likedVideos.value.filter(video => video.post_id != null)

    // Track auditions like with Fathom for members users
    if (isAuditionsVideo && typeof fathom !== 'undefined' && props.routePrefix === 'members2') {
        trackFathomEvent('member audition like')
    }

    isSubmittingStars.value = true

    const payload = {
        post_id: id,
        user_id: props?.auth?.user?.id ?? null,
        amount: amount,
        source: source || (isBuyout ? 'buyout' : 'give_stars'),
    }

    if (comment) {
        payload.comment = comment
    }

    if (interactionMethod) {
        payload.interaction_method = interactionMethod
    }

    axios
        .post(route(props.routePrefix + '.reels.give-stars'), payload)
        .then(function (response) {
            showStarVotingError.value = false
            starVotingErrorText.value = ''

            videos.value[currentIndex.value].likes = response.data.post.likes
            videos.value[currentIndex.value].star_goal_reached = response.data.post.star_goal_reached
            videos.value[currentIndex.value].sponsored_by_user = response.data.post.sponsored_by_user
            videos.value[currentIndex.value].stars_given_by_user = response.data.post.stars_given_by_user
            videos.value[currentIndex.value].is_new = response.data.post.is_new
            videos.value[currentIndex.value].is_near_goal = response.data.post.is_near_goal

            walletBalance.value = Number(response.data.remainingBalance)

            isSubmittingStars.value = false

            if (isBuyout) {
                isGiveStarsModalOpen.value = false
                showStarVotingSuccess.value = false
                isBuyoutSuccessModalOpen.value = true
            } else if (!comment && shouldShowCommentPrompt()) {
                pendingCommentPromptSuccess.value = true
                isCommentPromptModalOpen.value = true
            } else {
                if (comment) {
                    userHasCommented.value = true
                }
                showStarVotingSuccess.value = true
            }

            if (response.data.sga_awarded) {
                window.dispatchEvent(new CustomEvent('show-sga-overlay', { detail: response.data.sga_awarded }))
            }

            // Refresh sponsor data so the list reflects the new contribution
            fetchTopSponsors(id)

            const videoProps = videos.value[currentIndex.value];
            videoProps.stars_given = amount;
            videoProps.user_remaining_balance = walletBalance.value;
            trackAuditionAnalyticsEvent(isBuyout ? 'Buyout Completed' : 'Stars Given', videoProps)
        })
        .catch(error => {
            console.error(error)

            isSubmittingStars.value = false
            showStarVotingSuccess.value = false
            showStarVotingError.value = true

            const status = error.response?.status
            if (status === 401 || status === 419) {
                starVotingErrorText.value = 'Your session has expired. Please refresh the page and try again.'
            } else {
                starVotingErrorText.value = error.response?.data?.message || 'Sorry, an error occurred while giving stars. Please try again.'
            }
        })
}

const handleBuyOutPurchase = ({ starsNeeded, starsDeficit, currentBalance }) => {
    const baseUrl = pageProps.buyNowPurchaseBaseUrl
    if (!baseUrl) {
        isGiveStarsModalOpen.value = false
        buyStarsSource.value = 'buyout_fallback'
        isBuyStarsModalOpen.value = true
        return
    }

    const costInCents = starsDeficit * 25
    const optionCode = 'segpay-onetime-' + costInCents
    const purchaseUrl = baseUrl.replace('__OPTION_CODE__', optionCode)

    const auditionId = videos.value[currentIndex.value]?.id
    const returnParams = new URLSearchParams(window.location.search)
    returnParams.set('buyoutComplete', '1')
    const returnUrl = window.location.pathname + '?' + returnParams.toString()

    const separator = purchaseUrl.includes('?') ? '&' : '?'
    const params = new URLSearchParams({
        stars_needed: starsNeeded,
        stars_deficit: starsDeficit,
        current_balance: currentBalance,
        audition_id: auditionId,
        psc2: returnUrl,
        psc3: 'buyout',
    })

    window.location.href = purchaseUrl + separator + params.toString()
}

// Begin V2 Changes
const isJoinModalOpen = ref(false)
const isGoldJoinModalOpen = ref(false)
const isBuyStarsModalOpen = ref(false)
const isBuyoutSuccessModalOpen = ref(false)
const isGiveStarsModalOpen = ref(false)
const isFirstStarModalOpen = ref(false)
const isStarPowerModalOpen = ref(false)
const isCastingSignupModalOpen = ref(false)
const giveStarsInitialAmount = ref(null)

const isCommentPromptModalOpen = ref(false)
const pendingCommentPromptSuccess = ref(false)
const userHasCommented = ref(pageProps.userHasCommented ?? false)

const COMMENT_PROMPT_DISMISSED_KEY = 'comment_prompt_dismissed'
const COMMENT_PROMPT_COOLDOWN_KEY = 'comment_prompt_cooldown_until'

const shouldShowCommentPrompt = () => {
    if (userHasCommented.value) {
        return false
    }
    try {
        if (localStorage.getItem(COMMENT_PROMPT_DISMISSED_KEY) === '1') {
            return false
        }
        const cooldownUntil = localStorage.getItem(COMMENT_PROMPT_COOLDOWN_KEY)
        if (cooldownUntil && Date.now() < parseInt(cooldownUntil, 10)) {
            return false
        }
    } catch (e) {}
    return true
}

const setCommentPromptCooldown = (days) => {
    try {
        const ms = days * 24 * 60 * 60 * 1000
        localStorage.setItem(COMMENT_PROMPT_COOLDOWN_KEY, (Date.now() + ms).toString())
    } catch (e) {}
}

const handleCommentPromptSubmit = (commentText) => {
    isCommentPromptModalOpen.value = false
    userHasCommented.value = true

    const id = videos.value[currentIndex.value].id
    axios.post(route(props.routePrefix + '.reels.add-comment'), {
        post_id: id,
        comment: commentText,
    }).then(({ data }) => {
        if (data.sga_awarded) {
            window.dispatchEvent(new CustomEvent('show-sga-overlay', { detail: data.sga_awarded }))
        }
    }).catch(() => {})

    if (pendingCommentPromptSuccess.value) {
        pendingCommentPromptSuccess.value = false
        showStarVotingSuccess.value = true
    }
}

const handleCommentPromptClose = () => {
    isCommentPromptModalOpen.value = false
    try { localStorage.setItem(COMMENT_PROMPT_DISMISSED_KEY, '1') } catch (e) {}

    if (pendingCommentPromptSuccess.value) {
        pendingCommentPromptSuccess.value = false
        showStarVotingSuccess.value = true
    }
}

const handleCommentPromptMaybeLater = () => {
    isCommentPromptModalOpen.value = false
    setCommentPromptCooldown(3)

    if (pendingCommentPromptSuccess.value) {
        pendingCommentPromptSuccess.value = false
        showStarVotingSuccess.value = true
    }
}

const isSfwJoinModalOpen = ref(false)
const isOpenOnSwipe = ref(false) // sets if star power modal was opened on swipe
const transitionName = ref('slide-up')

// Instagram-style gesture-following swipe
const reelViewportRef = ref(null)
const currentVideoPlayerRef = ref(null)
const slideHeight = ref(600) // Fallback, updated in onMounted
const dragOffset = ref(0)
const isDragging = ref(false)
const gestureClaimed = ref(false)
const isAnimating = ref(false)
const touchStartY = ref(0)
const touchStartX = ref(0)
const lastTouchY = ref(0)
const lastTouchTime = ref(0)
const SWIPE_THRESHOLD = 0.15 // 15% of slide height to commit (reduced for faster swiping)
const GESTURE_CLAIM_THRESHOLD = 8
let resizeObserver = null

// Reactive window dimensions so reelViewportStyle updates on resize
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 768)
const handleWindowResize = () => {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
}

// Computed properties for current video player state
const currentVideoProgress = computed(() => currentVideoPlayerRef.value?.videoProgress ?? 0)
const currentVideoIsMuted = computed(() => currentVideoPlayerRef.value?.isMuted ?? true)

// Handlers for video controls
const handleVideoToggleMute = () => {
    currentVideoPlayerRef.value?.handleToggleMute()
}

const handleVideoSeek = (percentage) => {
    currentVideoPlayerRef.value?.handleSeek(percentage)
}

// The give-stars success modal pauses the underlying reel whenever it
// reveals the cinematic thank-you video so the two videos don't play
// audio simultaneously. Resume is paired by the modal on every dismissal
// path (close, swipe, give-more-stars, view-top-sponsors).
const handleMainVideoPause = () => {
    currentVideoPlayerRef.value?.pause?.()
}

const handleMainVideoResume = () => {
    currentVideoPlayerRef.value?.play?.()
}

const handleWatchReleasedSceneClick = () => {
    trackAuditionAnalyticsEvent('Watch Scene Button Clicked', currentVideoData.value)
    window.open(route(props.routePrefix + '.scene.show', currentVideoData.value.scene_slug), '_blank')
}

const reelTrackStyle = computed(() => {
    const base = -slideHeight.value
    const offset = dragOffset.value
    const transition = isAnimating.value ? 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
    return {
        transform: `translateY(calc(${base}px + ${offset}px))`,
        transition,
    }
})

const reelViewportStyle = computed(() => {
    if (typeof window === 'undefined') {
        return { width: '100%', height: '100vh', margin: '0 auto' }
    }
    if (windowWidth.value >= 1024) {
        return { width: '560px', height: '100dvh', margin: '0 auto' }
    }
    return { width: '100%', height: `${windowHeight.value}px`, margin: '0 auto' }
})
const menuStyle = computed(() => {
    if (typeof window === 'undefined') {
        return { width: '100%', height: '68px', margin: '0 auto' }
    }
    if (windowWidth.value >= 1024) {
        return { width: '560px', height: '68px', margin: '0 auto' }
    }
    return { width: '100%', height: `68px`, margin: '0 auto' }
})

const updateSlideHeight = () => {
    if (reelViewportRef.value) {
        slideHeight.value = reelViewportRef.value.clientHeight
    }
}


const getPointerY = (e) => (e.touches ? e.touches[0].clientY : e.clientY)
const getPointerX = (e) => (e.touches ? e.touches[0].clientX : e.clientX)

const onReelSwipeStart = (e) => {
    if (e.target.tagName === 'BUTTON' || e.target.closest('button') || e.target.closest('a')) return
    if (isAnimating.value) return
    isDragging.value = true
    gestureClaimed.value = false
    touchStartY.value = getPointerY(e)
    touchStartX.value = getPointerX(e)
    lastTouchY.value = touchStartY.value
    lastTouchTime.value = Date.now()
}

const onReelSwipeMove = (e) => {
    if (!isDragging.value) return
    const y = getPointerY(e)
    const x = getPointerX(e)
    const verticalDelta = Math.abs(y - touchStartY.value)
    const horizontalDelta = Math.abs(x - touchStartX.value)

    if (!gestureClaimed.value && verticalDelta >= GESTURE_CLAIM_THRESHOLD && verticalDelta > horizontalDelta) {
        gestureClaimed.value = true
    }
    if (gestureClaimed.value) {
        e.preventDefault()
        // On mobile: only claim gesture (prevent scroll), don't follow finger - avoids weird iOS slide animation
        if (!isMobileDevice.value) {
            const delta = touchStartY.value - y // positive = finger up = reveal next
            const maxDrag = slideHeight.value * 0.5
            dragOffset.value = Math.max(-maxDrag, Math.min(maxDrag, delta))
        }
    }
    lastTouchY.value = y
    lastTouchTime.value = Date.now()
}

const onReelSwipeEnd = (e) => {
    if (!isDragging.value) return
    const endY = e.changedTouches?.[0]?.clientY ?? lastTouchY.value
    const delta = touchStartY.value - endY
    const threshold = slideHeight.value * SWIPE_THRESHOLD
    const shouldGoNext = nextVideoData.value && delta > threshold
    const shouldGoPrev = previousVideoData.value && delta < -threshold

    if (gestureClaimed.value) {
        e.preventDefault()
        e.stopPropagation()
    }

    isDragging.value = false

    if (shouldGoNext) {
        isAnimating.value = true
        dragOffset.value = -slideHeight.value
        setTimeout(() => {
            commitToNextVideo()
            dragOffset.value = 0
            isAnimating.value = false
        }, 250)
    } else if (shouldGoPrev) {
        isAnimating.value = true
        dragOffset.value = slideHeight.value
        setTimeout(() => {
            commitToPreviousVideo()
            dragOffset.value = 0
            isAnimating.value = false
        }, 250)
    } else {
        isAnimating.value = true
        dragOffset.value = 0
        setTimeout(() => { isAnimating.value = false }, 200)
    }
}

const commitToNextVideo = () => {
    if (!nextVideoData.value) {
        // Even if there's no next video in the array, try to load more videos
        // This handles the case where we're at the end but more videos might be available from the server
        loadMoreVideos(currentIndex.value)
        return
    }
    transitionName.value = 'slide-up'
    updateBrowserUrl(nextVideoData.value)
    if (swipeLimit.value > 0) {
        const video = videos.value[currentIndex.value]
        recordUnlockedVideoId(video)
        if (video && !video.is_ad) {
            if (!countedVideoIds.value.includes(video.id)) {
                bumpSwipeCount(video)
                countedVideoIds.value = [...countedVideoIds.value, video.id]
                saveCountedIdsToStorage()
            }
            lastBumpedVideoId.value = video.id
        }
    }
    // Increment index - the watch on currentIndex will handle loading more videos
    currentIndex.value++
    trackPageView()
}

const commitToPreviousVideo = () => {
    if (!previousVideoData.value) return
    transitionName.value = 'slide-down'
    updateBrowserUrl(previousVideoData.value)
    if (swipeLimit.value > 0) {
        const video = videos.value[currentIndex.value]
        recordUnlockedVideoId(video)
        if (video && !video.is_ad) {
            if (!countedVideoIds.value.includes(video.id)) {
                bumpSwipeCount(video)
                countedVideoIds.value = [...countedVideoIds.value, video.id]
                saveCountedIdsToStorage()
            }
            lastBumpedVideoId.value = video.id
        }
    }
    currentIndex.value--
    trackPageView()
}

const nextVideoData = computed(() => {
    if (currentIndex.value === videos.value.length - 1) {
        return null
    }

    return videos.value[currentIndex.value + 1]
})
const previousVideoData = computed(() => {
    if (currentIndex.value === 0) {
        return null
    }

    return videos.value[currentIndex.value - 1]
})

const showNextVideo = () => {
    if (!nextVideoData.value || isAnimating.value) return
    if (isDebugMode.value) console.log('NEXT VIDEO SWIPE LIMIT', swipeLimit.value)
    isAnimating.value = true
    dragOffset.value = -slideHeight.value
    setTimeout(() => {
        commitToNextVideo()
        dragOffset.value = 0
        isAnimating.value = false
    }, 250)
}

const showPreviousVideo = () => {
    if (!previousVideoData.value || isAnimating.value) return
    if (isDebugMode.value) console.log('PREVIOUS VIDEO SWIPE LIMIT', swipeLimit.value)
    isAnimating.value = true
    dragOffset.value = slideHeight.value
    setTimeout(() => {
        commitToPreviousVideo()
        dragOffset.value = 0
        isAnimating.value = false
    }, 250)
}

const updateBrowserUrl = (videoData) => {
    if (videoData) {
        const url = currentTag.value.slug !== 'all'
            ? `${videoData.slug}?tag=${currentTag.value.slug}&sort=${pageProps.sort}`
            : videoData.slug

        window.history.replaceState({}, '', url)
    }
}

// Watch for changes in currentIndex to update meta tags
watch(() => currentIndex.value, (newIndex, oldIndex) => {
    updateMetaTags()

    // Fetch sponsors for the new video
    if (videos.value[newIndex]?.id) {
        fetchTopSponsors(videos.value[newIndex].id)
    }

    // Proactively load more videos when approaching the end
    // This ensures we load videos before running out, especially important on mobile
    // Only call on actual index changes (not initial mount) and when we have videos
    if (oldIndex !== undefined && newIndex !== undefined && videos.value && videos.value.length > 0) {
        loadMoreVideos(newIndex)
    }

    // Only increment video swipe count for actual video changes (not initial load)
    if (oldIndex !== undefined && newIndex !== undefined && videos.value && videos.value[newIndex] && !videos.value[newIndex].is_ad) {
        //const newSwipeCount = incrementStarModalVideoSwipeCount()

        //if (isDebugMode.value) {
        //    console.log('Video swipe count incremented to:', newSwipeCount)
        //}

        // Check if we should show the star modal for auditions videos (only on actual video changes)
        const isAuditionsVideo = currentVideoHasCategory(newIndex, 'Auditions')
        //const shouldShow = trackSwipeCycle()
        const shouldShow = trackSwipeTriggers()

        if (isDebugMode.value) {
            console.log('Star Modal Video Change Debug:', {
                newIndex,
                oldIndex,
                isAuditionsVideo,
                shouldShow,
                videoCategories: videos.value[newIndex]?.categories,
                currentFeed: props.tag?.slug || 'unknown'
            })
        }

        // if (isAuditionsVideo && shouldShow) {
        //     // Small delay to ensure video is loaded
        //     setTimeout(() => {
        //         if (isDebugMode.value) {
        //             console.log('Showing star modal for auditions video on video change')
        //         }
        //         //showStarModal.value = true
        //         // Show Star Power Modal instead of Star Modal to try and get more users to join
        //         isStarPowerModalOpen.value = true;
        //
        //         // Check if this is the 3rd or 9th video modal
        //         const videoSwipeCount = getStarModalVideoSwipeCount()
        //         if (videoSwipeCount === VIDEO_SWIPE_TRIGGER_COUNT_3RD) {
        //             if (isDebugMode.value) {
        //                 console.log('Showing 3rd video star modal')
        //             }
        //             setThirdModalShown()
        //         } else if (videoSwipeCount === VIDEO_SWIPE_TRIGGER_COUNT_9TH) {
        //             if (isDebugMode.value) {
        //                 console.log('Showing 9th video star modal')
        //             }
        //             setNinthModalShown()
        //         }
        //     }, 500)
        // }

        if (isAuditionsVideo && shouldShow) {
            isStarPowerModalOpen.value = true
            isOpenOnSwipe.value = true
            joinStarPowerSource.value = 'Star_Power_Popup'
            trackAuditionAnalyticsEvent('Star Power Modal Displayed', currentVideoData.value)
            trackFathomEvent('Star Power Modal Displayed')
        }
    }
})

const handleVideoClickEvent = () => {
    if (isCurrentPostExpired.value) return
    if (shouldVideoBeLocked.value(currentIndex.value)) {
        showTimeoutPopup.value = true
    }
}

function bumpSwipeCount() {
    if (isDebugMode.value) {
        console.log('BUMP SWIPE COUNT')
    }

    if (hasReelsSubscription.value && !isTrialUser.value) {
        return
    }

    // If list of counted video ids is full, we are in timeout state
    // block early to prevent "delay" in api call
    if (countedVideoIds.value.length >= swipeLimit.value) {
        timeoutLimitReached.value = true
        trackFathomEvent(fathomTimeoutViewMessage.value);
    }

    axios.post(route(props.routePrefix + '.reels.timeout')).then(response => {
        timeoutLimitReached.value = response.data.limit_reached;
        currentSwipeCount.value = response.data.current_swipe_count;

        if (response.data.limit_reached) {
            try { localStorage.setItem(LIMIT_REACHED_STORAGE_KEY, '1') } catch (e) {}

            showTimeoutPopup.value = false;

            // Only show popup on first time
            if (timeoutPopupShowCount.value === 0) {
                timeoutPopupShowCount.value++;
            }
        }
    })
}

async function loadMoreVideos(currentVideoIndex) {
    if (isDebugMode.value) {
        console.log('--- load more videos, current index', currentVideoIndex)
        console.log('--- load more videos, next loading', nextLoading)
    }

    if (nextLoading) {
        return
    }

    const totalVideos = videos.value.length
    // Calculate videos left based on current index (which is now the index we just moved to)
    // We want to load more when we're within loadMoreThreshold of the end
    let videosLeft = totalVideos - currentVideoIndex - 1

    if (isDebugMode.value) {
        console.log('--- load more videos, total videos', totalVideos)
        console.log('--- load more videos, videos left', videosLeft)
        console.log('--- load more videos, load more threshold', loadMoreThreshold)
    }

    // Load more videos when we're within the threshold (videosLeft <= loadMoreThreshold)
    // This ensures we load proactively before running out
    if (videosLeft > loadMoreThreshold) {
        return
    }

    nextLoading = true
    try {
        if (isDebugMode.value) {
            console.log('==LOADING MORE VIDEOS==')
        }

        const response = await axios.post(nextRoute())

        if (isDebugMode.value) {
            console.log('==LOADING MORE VIDEOS RESPONSE==', response.data)
        }

        // Check if response has the expected structure
        if (!response.data.data || !Array.isArray(response.data.data)) {
            console.error('Invalid response format:', response.data)
            nextLoading = false
            return
        }

        // If the data array is empty, just finish loading (no more videos available)
        if (response.data.data.length === 0) {
            nextLoading = false
            return
        }

        const newVideos = response.data.data.map((video, index) => ({
            ...video,
            NewLoaded: index === 0 ? true : false,
        }))

        const existingIds = new Set(videos.value.map(v => v.id));
        const filteredNewVideos = newVideos.filter(v => !existingIds.has(v.id));
        videos.value = [...videos.value, ...filteredNewVideos];
        nextPage.value = response.data.next_page;

        if (isDebugMode.value) {
            console.log('--- load more videos, new videos', newVideos)
            console.log('--- load more videos, filtered new videos', filteredNewVideos)
            console.log('--- load more videos, videos', videos.value)
        }
    } catch (error) {
        console.error(error)
    } finally {
        nextLoading = false
    }
}

// const containerStyle = computed(() => {
//     // Desktop: fixed dimensions
//     if (window.innerWidth >= 1024) {
//         return {
//             width: '560px',
//             margin: '0 auto'
//         }
//     }
//
//     // Mobile: use visual viewport height
//     return {
//         width: '100%',
//         margin: '0 auto'
//     }
// })

// Debounce wheel events to prevent too rapid scrolling
let wheelTimeout = null
const wheelDebounceDelay = 400 // milliseconds between allowed wheel actions (reduced for faster trackpad swiping)

const handleWheelNavigation = (event) => {
    // Ignore wheel on mobile - touch handles swipe there (matches Feed.vue)
    if (isMobileDevice.value) return

    // Prevent default scroll behavior
    event.preventDefault()

    // Reduced threshold for more responsive trackpad swiping (was 65)
    if (Math.abs(event.deltaY) <= 65) return

    // If we're still in the debounce period, ignore this wheel event
    if (wheelTimeout) return

    const delta = event.deltaY

    if (delta > 0) {
        if (nextVideoData.value) {
            showNextVideo()
            wheelTimeout = setTimeout(() => { wheelTimeout = null }, wheelDebounceDelay)
        }
    } else if (delta < 0) {
        if (previousVideoData.value) {
            showPreviousVideo()
            wheelTimeout = setTimeout(() => { wheelTimeout = null }, wheelDebounceDelay)
        }
    }
}

// Debounce keyboard events to prevent too rapid navigation
let keyTimeout = null
const keyDebounceDelay = 500 // milliseconds between allowed key presses

const handleKeyboardNavigation = (event) => {
    // Only handle arrow keys and prevent default for them
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        return
    }

    // Prevent default scroll behavior for arrow keys
    event.preventDefault()

    // If we're still in the debounce period, ignore this key press
    if (keyTimeout) {
        return
    }

    switch (event.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
            // Previous video
            if (previousVideoData.value) {
                showPreviousVideo()

                // Set debounce timeout
                keyTimeout = setTimeout(() => {
                    keyTimeout = null
                }, keyDebounceDelay)
            }
            break

        case 'ArrowDown':
        case 'ArrowRight':
            // Next video
            if (nextVideoData.value) {
                showNextVideo()

                // Set debounce timeout
                keyTimeout = setTimeout(() => {
                    keyTimeout = null
                }, keyDebounceDelay)
            }
            break
    }
}

const handleJoinModalClose = () => {
    // isJoinModalOpen.value = false
    isStarPowerModalOpen.value = false
    isOpenOnSwipe.value = false
    joinStarPopupSource.value = ''
    clearJoinOptions()
}

const isBackArrowNavigation = ref(false)

const handleBackArrowClick = () => {
    if (isDebugMode.value) {
        console.log('🔙 Back arrow clicked - setting restore flag')
    }

    // Set internal flag
    isBackArrowNavigation.value = true

    // Set flags in sessionStorage with multiple writes to ensure persistence
    sessionStorage.setItem('shouldRestoreBrowseState', 'true')
    sessionStorage.setItem('cameFromIndex', 'true')
    sessionStorage.setItem('backArrowNavTimestamp', Date.now().toString())

    // Force sessionStorage to persist by reading it back immediately
    const verify1 = sessionStorage.getItem('shouldRestoreBrowseState')
    const verify2 = sessionStorage.getItem('cameFromIndex')

    if (isDebugMode.value) {
        console.log('✅ Flags set and verified:', {
            shouldRestore: verify1,
            cameFromIndex: verify2,
            timestamp: sessionStorage.getItem('backArrowNavTimestamp')
        })
    }

    // If user came from another page (e.g. ViewCasting), go back there instead
    const referrer = sessionStorage.getItem('feedReferrer')
    if (referrer) {
        sessionStorage.removeItem('feedReferrer')
        router.visit(referrer, { preserveScroll: false, preserveState: false })
        return
    }

    // Navigate immediately - no timeout needed
    router.visit(getMainAuditionsUrl(isUserLoggedIn.value, props.routePrefix), {
        preserveScroll: false,
        preserveState: true,
        onBefore: () => {
            // Re-set flags right before navigation starts
            sessionStorage.setItem('shouldRestoreBrowseState', 'true')
            sessionStorage.setItem('cameFromIndex', 'true')

            if (isDebugMode.value) {
                console.log('🚀 onBefore: Re-confirming flags before navigation')
            }
        },
        onStart: () => {
            // One more time when navigation actually starts
            sessionStorage.setItem('shouldRestoreBrowseState', 'true')
            sessionStorage.setItem('cameFromIndex', 'true')

            if (isDebugMode.value) {
                console.log('🚀 onStart: Final flag confirmation')
            }
        }
    })
}

const handleGoldJoinModalOpen = (source) => {
    isGoldJoinModalOpen.value = true
    if (source === 'gold_upgrade') {
        joinGoldUpgradeSource.value = 'Star_Popup_Gold_Upgrade'
    }

    trackAuditionAnalyticsEvent('Not Enough Stars Button Clicked', currentVideoData.value)
}

const handleGoldJoinModalClose = () => {
    isGoldJoinModalOpen.value = false
    joinGoldUpgradeSource.value = ''
    clearJoinOptions()
}

const handleBuyStarPack = (pack) => {
    if (!pack.purchaseUrl) return
    const currentSearch = window.location.search
    const returnParams = new URLSearchParams(currentSearch)
    returnParams.set('openGiveStars', '1')
    const returnUrl = window.location.pathname + '?' + returnParams.toString()
    const source = buyStarsSource.value || 'feed'
    openStarPackPurchaseUrl(pack, source, returnUrl)
}

const handleStarPowerModalClose = (trigger) => {
    isStarPowerModalOpen.value = false
    isOpenOnSwipe.value = false
    joinStarPowerSource.value = ''
    joinStarPopupSource.value = ''
    joinCreateCastingSource.value = ''

    if (trigger && trigger === 'dont-show-again') {
        trackAuditionAnalyticsEvent('Dont Show Star Power Modal Clicked', currentVideoData.value)
        setSwipeCyclePause();
        trackFathomEvent('Dont Show Star Power Modal Clicked')
    } else {
        trackAuditionAnalyticsEvent('Star Power Modal Closed', currentVideoData.value)
        trackFathomEvent('Star Power Modal Closed')
    }
}

// Castings Modal
const isCreateCastingModalOpen = ref(false)
const isCastingLoading = ref(false)
const isCastingSubmitted = ref(false)
const castingError = ref('')
const castingFieldErrors = ref({})
const castingSimilarWarning = ref(null)
const castingModalRef = ref(null)

const handleCastingSubmit = async (formData) => {
    if (!isUserLoggedIn.value) {
        isCastingSignupModalOpen.value = true
        return
    }

    isCastingLoading.value = true
    castingError.value = ''
    castingFieldErrors.value = {}
    castingSimilarWarning.value = null

    try {
        const response = await axios.post(route(props.routePrefix + '.reels.casting.store'), {
            title:                  formData.title || null,
            performers:             formData.performers.map(p => p.id),
            performer_description:  formData.performerDescription || null,
            format:                 formData.format || null,
            tags:                   formData.tags,
            description:            formData.description || null,
            setting:                formData.setting,
            director_notes:         formData.directorNotes || null,
            force:                  formData.force || false,
        })

        isCastingSubmitted.value = true

        // Update user balance
        if (response.data.remaining_balance !== undefined) {
            walletBalance.value = Number(response.data.remaining_balance)
        }

        castingModalRef.value?.clearDraftCookie()
    } catch (e) {
        if (e.response?.status === 409 && e.response?.data?.similar) {
            castingSimilarWarning.value = e.response.data.existing
        } else if (e.response?.status === 422 && e.response?.data?.errors) {
            // Laravel validation errors - extract field-specific errors
            const errors = e.response.data.errors
            castingFieldErrors.value = Object.keys(errors).reduce((acc, key) => {
                acc[key] = Array.isArray(errors[key]) ? errors[key][0] : errors[key]
                return acc
            }, {})
            // Set general error message if no field-specific errors
            if (Object.keys(castingFieldErrors.value).length === 0) {
                castingError.value = e.response?.data?.message || 'Please fix the errors below.'
            }
        } else {
            castingError.value = e.response?.data?.message || 'Something went wrong. Please try again.'
        }
    } finally {
        isCastingLoading.value = false
    }
}

const handleBuyStars = () => {
    isCreateCastingModalOpen.value = false
    if (isUserLoggedIn.value) {
        buyStarsSource.value = 'create_casting'
        isBuyStarsModalOpen.value = true
    } else {
        isStarPowerModalOpen.value = true
    }
}

const handleCreateCastingClick = () => {
    trackFathomEvent(getFathomPostPrefix(props.routePrefix) + '_Create_Casting_Modal_Opened')
    joinCreateCastingSource.value = 'Create_Casting_Popup'
    isCreateCastingModalOpen.value = true
}

const handleCastingSignupModalClose = () => {
    isCastingSignupModalOpen.value = false

    trackAuditionAnalyticsEvent('Casting Signup Modal Closed', currentVideoData.value)
    trackFathomEvent('Casting Signup Modal Closed')
}

</script>

<template>
    <div class="flex flex-1 max-w-[1522px] mx-auto w-full">
        <!-- Debug info -->
        <div v-if="isDebugMode" class="fixed top-0 left-0 bg-black text-white p-2 z-50 text-xs">
            Title: {{ metaInfo.title }}<br>
            Current Video: {{ currentIndex }}<br>
            Videos Length: {{ videos.length - 1 }}<br>
            <strong class="text-yellow-400">Available Stars: {{ walletBalance.toLocaleString() }}</strong><br>
            <button @click="clearTimeoutClick" class="bg-red-800 text-white px-2 py-1 text-xs mt-1 ml-1">
                Clear Timeout
            </button>
            <div class="mt-2">
                Lock all after limit: <strong>{{ lockAllAfterLimit ? 'ON' : 'OFF' }}</strong>
                <button @click="lockAllAfterLimit = !lockAllAfterLimit" class="bg-red-500 text-white px-2 py-1 text-xs ml-2">
                    Toggle
                </button>
                <span class="ml-2">timeoutLimitReached: {{ timeoutLimitReached }}</span>
            </div>
        </div>
        <div v-if="isDebugMode" class="fixed top-[200px] left-0 bg-black text-white p-2 z-50 text-xs">
            Popup Debug:<br>
            hasReelsSubscription: {{ hasReelsSubscription }}<br>
            isTrialUser: {{ isTrialUser }}<br>
            showTimeoutPopup: {{ showTimeoutPopup }}<br>
            Popup should show: {{ !hasReelsSubscription && showTimeoutPopup }}<br>
            Popup show count: {{ timeoutPopupShowCount }}<br>
            Current swipe count: {{ currentSwipeCount }}<br>
            timeoutLimitReached: {{ timeoutLimitReached }}<br>
            pagePropsTimeoutLimitReached: {{ pageProps.timeoutLimitReached }}<br>
            swipeLimit: {{ swipeLimit }}<br>
            timeLimit: {{ timeLimit }}<br>
            Results from cache: {{ pageProps.read_from_cache }}<br>
            Cache key: {{ pageProps.cache_key }}<br>
            Is Randomized Feed: {{ pageProps.is_randomized_feed }}<br>
        </div>

        <!-- Left Section - 1045px with centered video -->
        <div id="left-side-mainVideo"
             ref="videoContainerRef"
             class="relative w-full lg:w-[1045px] flex justify-center items-start bg-black focus:outline-none focus:ring-0"
        >
            <!-- Top Chrome - fixed so it stays on screen when swiping (does not move with video) -->
            <div class="absolute top-0 left-0 right-0 z-30" :style="menuStyle">

                <div class="px-4 pt-4 pb-3">
                    <div class="relative flex items-center justify-between gap-3">
                        <button
                            @click="handleBackArrowClick"
                            class="w-10 h-10 rounded-full bg-white/5 border border-white/10 backdrop-blur-md flex items-center justify-center hover:bg-black/40 transition"
                            aria-label="Back"
                        >
                            <ArrowLeft class="w-5 h-5 text-white" />
                        </button>

                        <a :href="getMainAuditionsUrl(isUserLoggedIn, props.routePrefix)" class="absolute left-1/2 -translate-x-1/2 text-center z-30">
                            <p class="text-white/70 font-semibold capitalize text-[15px] tracking-tight">
                                {{ currentTag?.name }}
                            </p>
                        </a>

                        <!-- Kebab Menu - Mobile Only -->
                        <div class="lg:hidden">
                            <KebabMenu
                                :isUserLoggedIn="isUserLoggedIn"
                                :isGoldUser="isGoldUser"
                                :starBalance="walletBalance"
                                :requiresAgeVerification="requiresAgeVerification"
                                variant="compact"
                                :routePrefix="props.routePrefix"
                                @joinClick="isStarPowerModalOpen = true"
                                @goldJoinClick="buyStarsSource = 'menu'; isBuyStarsModalOpen = true"
                            />
                        </div>

                        <div class="hidden lg:block w-10 h-10" />
                    </div>
                </div>
            </div>

            <!-- Instagram-style reel stack: gesture-following swipe -->
            <!-- Touch for mobile, wheel for desktop (no mouse drag - matches Feed.vue, avoids double-nav on trackpad) -->
            <div
                ref="reelViewportRef"
                class="reel-viewport relative overflow-hidden touch-none select-none"
                :style="reelViewportStyle"
                @touchstart="onReelSwipeStart"
                @touchend="onReelSwipeEnd"
            >
                <div
                    class="reel-track"
                    :style="reelTrackStyle"
                >
                    <!-- Previous (placeholder when at start) -->
                    <div class="reel-slide shrink-0">
                        <VideoPlayer
                            v-if="previousVideoData || videos[currentIndex]"
                            :key="'prev-' + (previousVideoData?.id ?? videos[currentIndex]?.id)"
                            :videoData="previousVideoData || videos[currentIndex]"
                            :isUserLoggedIn="isUserLoggedIn"
                            :routePrefix="props.routePrefix"
                            :shouldAutoplay="false"
                            :shouldVideoBeLocked="shouldVideoBeLocked(previousVideoData ? currentIndex - 1 : currentIndex)"
                            :requiresAgeVerification="requiresAgeVerification"
                            @joinClick="isStarPowerModalOpen = true"
                            @videoClick="handleVideoClickEvent"
                        />
                    </div>
                    <!-- Current -->
                    <div class="reel-slide shrink-0">
                        <VideoPlayer
                            ref="currentVideoPlayerRef"
                            :key="'curr-' + videos[currentIndex]?.id"
                            :videoData="videos[currentIndex]"
                            :isUserLoggedIn="isUserLoggedIn"
                            :routePrefix="props.routePrefix"
                            :shouldAutoplay="shouldAutoplay(currentIndex)"
                            :shouldVideoBeLocked="shouldVideoBeLocked(currentIndex)"
                            :requiresAgeVerification="requiresAgeVerification"
                            :isExpired="isCurrentPostExpired"
                            @joinClick="isStarPowerModalOpen = true"
                            @videoClick="handleVideoClickEvent"
                        />
                    </div>
                    <!-- Next (placeholder when at end) -->
                    <div class="reel-slide shrink-0">
                        <VideoPlayer
                            v-if="nextVideoData || videos[currentIndex]"
                            :key="'next-' + (nextVideoData?.id ?? videos[currentIndex]?.id)"
                            :videoData="nextVideoData || videos[currentIndex]"
                            :isUserLoggedIn="isUserLoggedIn"
                            :routePrefix="props.routePrefix"
                            :shouldAutoplay="false"
                            :shouldVideoBeLocked="shouldVideoBeLocked(nextVideoData ? currentIndex + 1 : currentIndex)"
                            :requiresAgeVerification="requiresAgeVerification"
                            @joinClick="isStarPowerModalOpen = true"
                            @videoClick="handleVideoClickEvent"
                        />
                    </div>
                </div>
                <!-- Fixed fades - always visible during swipe (not tied to sliding VideoPlayers) -->
                <div class="pointer-events-none absolute inset-0 z-10">
                    <div class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 via-black/10 to-transparent" />
                    <div class="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                </div>
                <!-- Fixed gradient overlay - persistent during swipes, above video, below Bottom Overlay -->
                <div class="absolute inset-0 bg-gradient-to-b from-black/35 via-black/0 to-black/65 z-[15] pointer-events-none" />

                <!-- Fixed Controls - above gradient, persistent during swipes -->
                <VideoControls
                    v-if="currentVideoData && !shouldVideoBeLocked(currentIndex)"
                    :videoData="currentVideoData"
                    :isUserLoggedIn="isUserLoggedIn"
                    :routePrefix="props.routePrefix"
                    :sponsorCount="topSponsorsData.total || currentVideoData?.unique_sponsors_count || 0"
                    :commentCount="currentVideoData?.comments_count || 0"
                    :castingSlug="currentVideoData?.casting_slug"
                    :castingTitle="currentVideoData?.casting_title"
                    @starClick="handleStarClick"
                    @watchReleasedSceneClick="handleWatchReleasedSceneClick"
                    @viewComments="showSponsorsSheet = true"
                />

                <!-- Fixed Bottom Overlay - above gradient, persistent during swipes -->
                <VideoOverlay
                    v-if="currentVideoData"
                    :videoData="currentVideoData"
                    :isUserLoggedIn="isUserLoggedIn"
                    :routePrefix="props.routePrefix"
                    :progress="currentVideoProgress"
                    :isMuted="currentVideoIsMuted"
                    :shouldVideoBeLocked="shouldVideoBeLocked(currentIndex)"
                    @toggleMute="handleVideoToggleMute"
                    @seek="handleVideoSeek"
                    @nearGoalClick="handleNearGoalClick"
                />
            </div>

            <!-- Navigation Arrows - positioned on right edge of left section -->
            <NavigationArrows
                :canGoPrevious="!!previousVideoData"
                :canGoNext="!!nextVideoData"
                @next="showNextVideo"
                @previous="showPreviousVideo"
            />
        </div>

        <DesktopSidebar
            :videoData="currentVideoData"
            :moreAuditions="moreAuditions"
            :isUserLoggedIn="isUserLoggedIn"
            :isGoldUser="isGoldUser"
            :starBalance="walletBalance"
            :routePrefix="props.routePrefix"
            :sponsorsData="topSponsorsData"
            :requiresAgeVerification="requiresAgeVerification"
            @starClick="handleStarClick"
            @joinClick="isStarPowerModalOpen = true"
            @goldJoinClick="buyStarsSource = 'menu'; isBuyStarsModalOpen = true"
            @viewSponsors="showSponsorsSheet = true"
        />
    </div>

    <StarOverlay
        v-if="currentVideoHasCategory(currentIndex, 'Auditions')"
        :is-visible="showStarModal"
        :user-type="isUserLoggedIn ? 'member' : 'visitor'"
        @close="handleStarModalClose"
        @dont-show-again="handleStarModalDontShowAgain"
        @join-click="handleStarModalJoinClick"
        @start-starring="handleStarModalStartStarring"
    />

    <Timeout ref="timeoutRef"
             v-if="timeLimit > 0 && (isTrialUser || (!isUserLoggedIn && !hasReelsSubscription))"
             :timeLimit="timeLimit"
             :storageKey="timeoutStorageKey"
             :debug="isDebugMode"
             @timeLimitReached="handleTimeLimitReached" />

    <JoinModal
        :isOpen="isJoinModalOpen"
        :audition="currentVideoData"
        :source="joinStarPopupSource || joinDefaultSource"
        context="auditions"
        @close="handleJoinModalClose"
    />

    <JoinGoldModal :isOpen="isGoldJoinModalOpen"
                   :audition="currentVideoData"
                   :source="joinGoldUpgradeSource || joinGoldDefaultSource"
                   @close="handleGoldJoinModalClose"
    />

    <BuyStarsModal
        :isOpen="isBuyStarsModalOpen"
        :userBalance="walletBalance"
        :starPacks="pageProps.starPacks"
        @close="isBuyStarsModalOpen = false"
        @buy="handleBuyStarPack"
    />

    <GiveStarsModal
        :isOpen="isGiveStarsModalOpen"
        :audition="currentVideoData"
        :userBalance="walletBalance"
        :isGoldUser="isGoldUser"
        :loading="isSubmittingStars"
        :errorMessage="starVotingErrorText"
        :showSuccess="showStarVotingSuccess"
        :initialAmount="giveStarsInitialAmount"
        :routePrefix="props.routePrefix"
        @resetSuccess="showStarVotingSuccess = false"
        @close="isGiveStarsModalOpen = false; showStarVotingSuccess = false; giveStarsInitialAmount = null"
        @submit="submitStars"
        @buyStars="isGiveStarsModalOpen = false; buyStarsSource = 'give_stars_modal'; isBuyStarsModalOpen = true"
        @buyOutPurchase="handleBuyOutPurchase"
        @viewTopSponsors="showSponsorsSheet = true"
        @mainVideoPause="handleMainVideoPause"
        @mainVideoResume="handleMainVideoResume"
    />

    <BuyoutSuccessModal
        :isOpen="isBuyoutSuccessModalOpen"
        :audition="currentVideoData"
        @close="isBuyoutSuccessModalOpen = false"
        @viewTopSponsors="showSponsorsSheet = true"
    />

    <FirstStarModal
        :isOpen="isFirstStarModalOpen"
        :audition="currentVideoData"
        :userBalance="walletBalance"
        :loading="isSubmittingStars"
        :errorMessage="starVotingErrorText"
        :showSuccess="showStarVotingSuccess"
        :routePrefix="props.routePrefix"
        @resetSuccess="showStarVotingSuccess = false"
        @close="isFirstStarModalOpen = false; showStarVotingSuccess = false"
        @submit="submitStars"
        @buyStars="isFirstStarModalOpen = false; buyStarsSource = 'first_star_upsell'; isBuyStarsModalOpen = true"
        @openFullModal="isFirstStarModalOpen = false; showStarVotingSuccess = false; isGiveStarsModalOpen = true"
    />

    <!-- Top Sponsors Sheet -->
    <TopSponsorsSheet
        v-if="currentVideoData"
        :isOpen="showSponsorsSheet"
        :audition="currentVideoData"
        :sponsors="topSponsorsData"
        :isUserLoggedIn="isUserLoggedIn"
        :routePrefix="props.routePrefix"
        @close="showSponsorsSheet = false"
        @giveStars="showSponsorsSheet = false; handleStarClick('Top_Sponsors_Sheet')"
    />

    <StarPowerModal v-if="!isUserLoggedIn"
                    :isOpen="isStarPowerModalOpen"
                    :audition="currentVideoData"
                    :openOnSwipe="isOpenOnSwipe"
                    :source="joinStarPowerSource || joinStarPopupSource || joinCreateCastingSource || joinDefaultSource"
                    @close="handleStarPowerModalClose"
    />

    <CastingSignupModal :isOpen="isCastingSignupModalOpen"
                        :source="'Create_Casting_Popup'"
                        @close="handleCastingSignupModalClose"
    />

    <CommentPromptModal
        :isOpen="isCommentPromptModalOpen"
        :audition="currentVideoData"
        @close="handleCommentPromptClose"
        @submit="handleCommentPromptSubmit"
        @maybeLater="handleCommentPromptMaybeLater"
    />

    <SfwJoinModal :isOpen="isSfwJoinModalOpen"
                  :audition="currentVideoData"
                  @close="isSfwJoinModalOpen = false"
    />

    <CreateCastingWizardModal
        ref="castingModalRef"
        :isOpen="isCreateCastingModalOpen"
        :userBalance="walletBalance"
        :castingCost="Number(pageProps.castingsSettings?.default_star_cost ?? 5)"
        :loading="isCastingLoading"
        :errorMessage="castingError"
        :fieldErrors="castingFieldErrors"
        :submitted="isCastingSubmitted"
        :similarCasting="castingSimilarWarning"
        :isUserLoggedIn="isUserLoggedIn"
        @close="isCreateCastingModalOpen = false; isCastingSubmitted = false; castingError = ''; castingFieldErrors = {}; castingSimilarWarning = null"
        @dismissSimilar="castingSimilarWarning = null"
        @submit="handleCastingSubmit"
        @buyStars="handleBuyStars"
    />

    <Toast
        :show="showToast"
        position="bottomLeft"
        transitionName="fade"
        :transitionEnter="300"
        :transitionLeave="500"
        :text="toastText"
    />
</template>

<style>
.iub__us-widget {
    display: none !important;
    position: absolute;
    right: 0;
    border: 0;
}

body.reels-feed-page {
    overflow: hidden;
}

::backdrop {
    display: block;
    position: fixed;
    top: 0px;
}

@keyframes spin {
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

/* Slide up transition (next video) - video slides out of view like Instagram */
.slide-up-enter-active {
    transition: transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-up-leave-active {
    transition: transform 0.25s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.slide-up-enter-from {
    transform: translateY(100%);
}

.slide-up-leave-to {
    transform: translateY(-100%);
}

/* Slide down transition (previous video) */
.slide-down-enter-active {
    transition: transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-down-leave-active {
    transition: transform 0.25s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.slide-down-enter-from {
    transform: translateY(-100%);
}

.slide-down-leave-to {
    transform: translateY(100%);
}

/* Instagram-style reel stack */
.reel-viewport {
    position: relative;
    user-select: none;
}

.reel-track {
    display: flex;
    flex-direction: column;
    height: 300%;
}

.reel-slide {
    height: 33.333%;
    flex-shrink: 0;
}
</style>
